import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

import type { GroupDetails, RBACGroup } from '../../../types';
import { intuneGroupUrl } from '../../graph/intune';
import { useGetGroupDetailsQuery, useGetGroupMembersQuery } from '../../graph/services/graphApi';
import { vantagePointProjectUrl } from '../vantagePoint';

type Props = {
  role: RBACGroup | undefined;
};

// "Group type" the way the Entra/Intune portal labels it, derived from the same fields.
const groupTypeLabel = (details: GroupDetails | undefined) => {
  if (!details) return '…';
  if (details.groupTypes.includes('Unified')) return 'Microsoft 365';
  if (details.securityEnabled && details.mailEnabled) return 'Mail-enabled security';
  if (details.mailEnabled) return 'Distribution';
  return 'Security';
};

// "Membership type": Dynamic if the rule engine manages it, else Assigned.
const membershipTypeLabel = (details: GroupDetails | undefined) =>
  !details ? '…' : details.groupTypes.includes('DynamicMembership') ? 'Dynamic' : 'Assigned';

// Header summary for the selected role: its kind (group + membership type), description,
// a live member count, and a deep link to the group in the Intune portal. Count reflects
// committed directory state — staged adds/removes show inline in the Members panel, not
// here, until Submit. Shares the members cache entry with the Members panel (same query
// + arg), so no extra request for the count.
export default function RoleInfo({ role }: Props) {
  const { data: members } = useGetGroupMembersQuery(role?.id ?? '', { skip: !role });
  const { data: details } = useGetGroupDetailsQuery(role?.id ?? '', { skip: !role });

  if (!role) {
    return <div></div>;
  }

  // Only project roles carry a project number, so only they get a VantagePoint link.
  const projectUrl = vantagePointProjectUrl(role.displayName);

  // Two side-by-side label/value stacks (each its own grid) rather than one shared
  // grid, so the short left column packs tightly and isn't pulled apart by the
  // right column's wrapping Description — fills the header width, not its height.
  return (
    <div className="flex flex-row flex-wrap gap-x-16 gap-y-2 text-sm">
      <div className="grid grid-cols-[auto_auto] content-start items-start gap-x-3 gap-y-1">
        <span className="font-bold">Members:</span>
        <span>{members ? members.length : '…'}</span>
        <span className="font-bold">Group type:</span>
        <span>{groupTypeLabel(details)}</span>
        <span className="font-bold">Membership:</span>
        <span>{membershipTypeLabel(details)}</span>
      </div>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] content-start items-start gap-x-3 gap-y-1 flex-1 min-w-[16rem]">
        <span className="font-bold">Description:</span>
        <span className="whitespace-normal break-words">
          {role.description || <span className="opacity-60">N/A</span>}
        </span>
        <span className="font-bold">Portal:</span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <a
            className="link link-primary inline-flex items-center gap-1"
            href={intuneGroupUrl(role.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Intune
            <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
          </a>
          {projectUrl && (
            <a
              className="link link-primary inline-flex items-center gap-1"
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in VantagePoint
              <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
            </a>
          )}
        </span>
      </div>
    </div>
  );
}
